import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Spinner } from "@phosphor-icons/react";
import CvcAssessment from "@/assessment/CvcAssessment";
import { BookingScreen } from "@/components/nextmove/BookingForm";
import { AssessmentContactScreen } from "@/components/nextmove/AssessmentScreens";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function useLead(token) {
  const [state, setState] = useState({ phase: token ? "loading" : "gate" });
  useEffect(() => {
    if (!token) { setState({ phase: "gate" }); return; }
    setState({ phase: "loading" });
    fetch(`${API}/next-move/insights/${encodeURIComponent(token)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((lead) => setState({ phase: "assessment", lead }))
      .catch(() => setState({ phase: "gate", expired: true }));
  }, [token]);
  return [state, setState];
}

// Business Scaling Assessment on CVC: contact gate → 87 statements → scorecard → talk with Jonathan.
export default function Assessment() {
  const [params, setParams] = useSearchParams();
  const token = params.get("t");
  const source = params.get("from") === "approach" ? "approach" : "direct";
  const [content, setContent] = useState(null);
  const [state, setState] = useLead(token);

  useEffect(() => {
    fetch(`${API}/next-move/content`).then((r) => r.json()).then(setContent).catch(() => setContent(null));
  }, []);

  return (
    <div className="min-h-screen paper-grain px-4 py-8 md:py-12">
      <div className="max-w-3xl mx-auto">
        <Link to="/" data-testid="assessment-home-link" className="font-body text-sm text-cvc-ink-soft hover:text-cvc-navy">
          ← Cache Valley Consulting
        </Link>
        <div className="mt-4 bg-cvc-cream rounded-2xl border border-cvc-border shadow-sm overflow-hidden" data-testid="assessment-page">
          {state.phase === "loading" && (
            <div className="flex items-center gap-3 p-10 font-body text-cvc-ink-soft"><Spinner className="animate-spin" /> Loading…</div>
          )}
          {state.phase === "gate" && content && (
            <>
              {state.expired && (
                <p data-testid="assessment-link-expired" className="px-7 md:px-10 pt-6 font-body text-sm text-cvc-red">
                  That link isn't valid anymore. Add your details below to start fresh.
                </p>
              )}
              <AssessmentContactScreen content={content} source={source}
                onReady={(t) => setParams({ t })} />
            </>
          )}
          {state.phase === "assessment" && (
            <CvcAssessment token={token} onDiscuss={() => { setState({ ...state, phase: "booking" }); window.scrollTo({ top: 0 }); }} />
          )}
          {state.phase === "booking" && (
            <BookingScreen
              assessmentToken={token}
              prefill={{
                name: state.lead.name || "", business_name: state.lead.business_name || "",
                email: state.lead.email || "", phone: state.lead.phone || "",
              }}
              onBack={() => setState({ ...state, phase: "assessment" })}
              onDone={(label) => setState({ ...state, phase: "done", bookedLabel: label })}
            />
          )}
          {state.phase === "done" && (
            <div className="p-7 md:p-10 font-body" data-testid="assessment-booked">
              <p className="font-heading text-2xl text-cvc-navy">
              {state.bookedLabel ? `You're booked for ${state.bookedLabel}.` : "Thanks — Jonathan will reach out to confirm the time."}
            </p>
            {state.bookedLabel && <p className="mt-2 text-cvc-ink-soft">A calendar invite is on its way to your inbox.</p>}
              <Link to="/" className="mt-4 inline-block text-cvc-red underline underline-offset-4">Back to Cache Valley Consulting</Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
